import React from 'react'
import { Box } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import { AlbumSummary } from '../../types'
import { AlbumCard } from '../AlbumCard'
import { Skeleton } from '@/design-system'

export interface AlbumGridProps {
  albums: AlbumSummary[]
  loading?: boolean
  onSelect?: (album: AlbumSummary) => void
  /** Shows the dashed "New album" tile as the trailing grid cell. Defaults to true. */
  showNewTile?: boolean
}

const Grid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
  gap: 20,
  width: '100%',
})

const NewTile = styled('button')({
  all: 'unset',
  boxSizing: 'border-box',
  border: '1.5px dashed var(--ds-border-strong, var(--ds-border))',
  borderRadius: 'var(--ds-radius-lg)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 10,
  minHeight: 220,
  cursor: 'pointer',
  transition: `border-color var(--ds-duration-normal) var(--ds-ease), background var(--ds-duration-normal) var(--ds-ease)`,
  '&:hover': {
    borderColor: 'var(--ds-ink)',
    background: 'var(--ds-canvas)',
  },
  '&:hover .new-tile-plus': {
    transform: 'rotate(90deg)',
  },
})

const Plus = styled(Box)({
  width: 40,
  height: 40,
  borderRadius: '50%',
  background: 'var(--ds-inverse)',
  color: 'var(--ds-inverse-ink)',
  fontSize: 18,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: `transform var(--ds-duration-normal) var(--ds-ease)`,
})

const NewTitle = styled(Box)({
  fontSize: 13.5,
  fontWeight: 650,
  color: 'var(--ds-ink)',
})

const NewSubtitle = styled(Box)({
  fontSize: 11.5,
  color: 'var(--ds-ink-subtle)',
})

export function AlbumGrid({ albums, loading, onSelect, showNewTile = true }: AlbumGridProps) {
  const navigate = useNavigate()

  if (loading) {
    return (
      <Grid>
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} height='auto' sx={{ aspectRatio: '4 / 2.9', width: '100%' }} rounded='lg' />
        ))}
      </Grid>
    )
  }

  return (
    <Grid>
      {albums.map((album, i) => (
        <AlbumCard key={album.id} album={album} index={i} onClick={() => onSelect?.(album)} />
      ))}
      {showNewTile && (
        <NewTile type='button' onClick={() => navigate('/albums/new')}>
          <Plus className='new-tile-plus'>+</Plus>
          <NewTitle>New album</NewTitle>
          <NewSubtitle>Under a minute, key included</NewSubtitle>
        </NewTile>
      )}
    </Grid>
  )
}
